export const PROJECT_FIELD_TYPE = {
  ADDRESS: 0,
  STATUS_LIST: 1,
  FILE_LIST: 2,
  LIST: 3,
  TEXT: 4,
  MINI_CHART: 5,
  RANKING: 6,
  SKILLS: 7,
  TABLE: 8,
  CUSTOMER_TABLE: 9,
  BLUEPRINT_ELEMENTS: 10,
  BLUEPRINT_LIST: 11,
};

export const ICON = {
  [PROJECT_FIELD_TYPE.ADDRESS]: "mdi-map-marker",
  [PROJECT_FIELD_TYPE.FILE_LIST]: "mdi-file-multiple",
  [PROJECT_FIELD_TYPE.LIST]: "mdi-format-list-checkbox",
  [PROJECT_FIELD_TYPE.RANKING]: "mdi-star-circle",
  [PROJECT_FIELD_TYPE.TEXT]: "mdi-tooltip-text",
  [PROJECT_FIELD_TYPE.STATUS_LIST]: "mdi-circle",
  [PROJECT_FIELD_TYPE.SKILLS]: "mdi-shield-check",
  [PROJECT_FIELD_TYPE.TABLE]: "mdi-table",
  [PROJECT_FIELD_TYPE.CUSTOMER_TABLE]: "mdi-office-building",
  [PROJECT_FIELD_TYPE.BLUEPRINT_ELEMENTS]: "mdi-developer-board",
  [PROJECT_FIELD_TYPE.BLUEPRINT_LIST]: "mdi-file-pdf-box",
};

// SAM-273. Project custom fields have no schema anywhere: both project
// schemas are `strict: false` with no definition for any tenant-defined
// field, and the v1 write path (_updateProjectFields) stores whatever it is
// handed. The only record of a field's intended type is `type` here, in
// settings.projectFields.
//
// The renderers do not know that. Seven of the twelve types are read as
// arrays — .slice().push() in four byte-identical formatPreview copies, a
// [...spread] in file_list/blueprint_list, a for..of in skills, a .map() in
// the two question types — none of them guarded by Array.isArray. One field
// holding a string instead of a list therefore throws during render, and the
// error boundary sends the whole page to /error/500. That is both crashes QA
// reported on pink-legacy 2026-08-25.
//
// Same shape of fix, and the same reasoning, as withJobType/
// withQuestionSettings in services/api/jobs.js: normalise once at the funnel
// rather than guard ~10 read sites that would each render this empty state
// anyway.
const ARRAY_FIELD_TYPES = [
  PROJECT_FIELD_TYPE.FILE_LIST,
  PROJECT_FIELD_TYPE.LIST,
  PROJECT_FIELD_TYPE.SKILLS,
  PROJECT_FIELD_TYPE.TABLE,
  PROJECT_FIELD_TYPE.CUSTOMER_TABLE,
  PROJECT_FIELD_TYPE.BLUEPRINT_ELEMENTS,
  PROJECT_FIELD_TYPE.BLUEPRINT_LIST,
];

// ADDRESS is an object, TEXT a string, RANKING a number and STATUS_LIST a
// counter map — those are read as-is and are left alone.
//
// Verified against pink 2026-08-26: project 6a81b4b835c162974a402e38 holds two
// LIST fields storing a bare string ("חלקי המערכת", "ההה") — that is the
// a.push / f.map crash — and a FILE_LIST storing ["60058a6272fa551805e2cd23"],
// bare id strings, which is its legitimate shape.
const isObject = (value) => !!value && typeof value === "object";

// A LIST element's contract is {label, value}: list.js sorts on
// `a.label.localeCompare(b.label)` and both question types map to `m.label`.
// An element with no string label crashes the first and renders undefined in
// the second, so give it one without discarding whatever else it carries.
const isListItem = (element) =>
  isObject(element) && typeof element.label === "string";

const asListItem = (element) => {
  if (isListItem(element)) return element;
  if (isObject(element)) return { ...element, label: "" };

  return { label: String(element), value: element };
};

export const coerceFieldValue = (type, value) => {
  if (ARRAY_FIELD_TYPES.indexOf(type) === -1) return value;

  // Only LIST's renderers reach into the elements, and only LIST can
  // meaningfully hold one value that was stored unwrapped — so it is the one
  // type where a scalar is preserved rather than dropped.
  //
  // The others are made array-shaped and nothing more: FILE_LIST and
  // BLUEPRINT_LIST hold bare asset ids, TABLE and CUSTOMER_TABLE hold row
  // objects keyed by column, SKILLS holds skill documents. Giving any of those
  // a {label, value} element would render wrong rather than crash, which is
  // worse.
  if (type !== PROJECT_FIELD_TYPE.LIST) {
    return Array.isArray(value) ? value : [];
  }

  if (Array.isArray(value) && value.every(isListItem)) return value;

  const items = Array.isArray(value)
    ? value
    : value === null || value === undefined || value === ""
      ? []
      : [value];

  return items.map(asListItem);
};

export const coerceProjectFields = (project, projectFields) => {
  if (!project || !Array.isArray(projectFields)) return project;

  // project_list.js renders either one project or a list of them (a job can
  // span several). Spreading an array into an object would quietly turn it
  // into `{0: ..., 1: ...}`, so recurse instead.
  if (Array.isArray(project)) {
    return project.map((one) => coerceProjectFields(one, projectFields));
  }

  const coerced = { ...project };
  let changed = false;

  projectFields.forEach((field) => {
    if (!field || !(field.id in coerced)) return;

    const next = coerceFieldValue(field.type, coerced[field.id]);
    if (next !== coerced[field.id]) {
      coerced[field.id] = next;
      changed = true;
    }
  });

  return changed ? coerced : project;
};
